import { useAppDispatch } from 'app/hooks';
import { ahuBoardApi } from 'app/services/ahuBoardApi';
import { useBulkUpdateSectionsMutation } from 'app/services/ahuBoardFormsApi';
import type { IAhuBoardSection, IAhuBoardUnit } from 'models';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import PaginatedSelect from '../../forms/PaginatedSelect';
import { validateTunnel } from '../../validation/validationController';

/**
 * Phase 6e1 — multi-section bulk panel (§7.4.3, tab.multiply.sections.tmpl,
 * markup faithful): 4 paginated selects with common-value resolution
 * ("Varies" for mixed → null shown), change → POST bulk_update →
 * revalidate touched tunnels + local cache patch + `change:casing:section_<id>`
 * per section (liner/floor fields) + `sections:bulk_update` for per-section
 * select sync.
 */

const FIELDS = [
  ['casing-insulation-select', 'casing_insulation_id', 'ahu/casing/insulations', 'Casing Insulation'],
  ['casing-innerliner-select', 'casing_innerliner_id', 'ahu/casing/liner_metal_sheets', 'Casing Inner Liner'],
  ['floor-innerliner-select', 'floor_innerliner_id', 'ahu/floor/metal_sheets', 'Floor Deck'],
  ['floor-finish-select', 'floor_finish_id', 'ahu/floor/finishes', 'Floor Deck Finish'],
] as const;

interface BulkSectionsPanelProps {
  ahu: IAhuBoardUnit;
  selectedSections: IAhuBoardSection[];
  mfgId: number;
}

const BulkSectionsPanel = ({ ahu, selectedSections, mfgId }: BulkSectionsPanelProps) => {
  const dispatch = useAppDispatch();
  const [bulkUpdate] = useBulkUpdateSectionsMutation();
  const ccid = ahu.construction_class_id ?? null;

  // getCommonValues (legacy tab.multiply.sections.js:80): all-same →
  // firstValue || 'None' (common-falsy keeps the '-----' default text);
  // differing → null → '.selected' shows 'Varies' (renderSelects:182).
  const commonValue = (field: string): { value: number | null; mixed: boolean } => {
    const values = selectedSections.map(
      (s) => (s as Record<string, unknown>)[field] as number | null | undefined
    );
    const first = values[0] ?? null;
    const allSame = values.every((v) => (v ?? null) === first);
    return { value: allSame ? first : null, mixed: !allSame };
  };

  const onBulkChange = (field: string, value: number | null) => {
    const sectionIds = selectedSections.map((s) => s.id);
    bulkUpdate({ section_ids: sectionIds, [field]: value })
      .unwrap()
      .then(() => {
        // Local attribute patch on every touched section row.
        const byTunnel = new Map<number, number[]>();
        selectedSections.forEach((s) => {
          const list = byTunnel.get(s.tunnel_id) ?? [];
          list.push(s.id);
          byTunnel.set(s.tunnel_id, list);
        });
        byTunnel.forEach((ids, tunnelId) => {
          dispatch(
            ahuBoardApi.util.updateQueryData('getTunnelSectionList', tunnelId, (rows) => {
              rows.forEach((row) => {
                if (ids.includes(row.id)) (row as Record<string, unknown>)[field] = value;
              });
            })
          );
          void dispatch(validateTunnel(tunnelId));
        });
        if (field !== 'casing_insulation_id') {
          sectionIds.forEach((id) => ahuBoardBus.emit(`change:casing:section_${id}`));
        }
        ahuBoardBus.emit('sections:bulk_update', sectionIds, field, value);
      })
      .catch(() => undefined);
  };

  return (
    <div className="section-cabinet-list menu-item ahu-left-container">
      <div className="inputs-group">
        <ul className="sublist">
          <li>
            <div className="single-line">
              <span className="label multiply-sctions-label">Multiple Sections</span>
            </div>
          </li>
          {FIELDS.map(([selectId, field, path, label], idx) => {
            const { value, mixed } = commonValue(field);
            return (
              <li key={field} data-select-index={idx}>
                <div className="single-line-with-custom-select">
                  <div id={selectId} className="select-container">
                    <PaginatedSelect
                      mfgId={mfgId}
                      path={path}
                      constructionClassId={ccid}
                      value={value}
                      onChange={(id) => onBulkChange(field, id)}
                      selectName={field}
                      selectClass="multi_section_field"
                      nullText={mixed ? 'Varies' : '-----'}
                    />
                  </div>
                  <span className="label">{label}</span>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default BulkSectionsPanel;
